import { gql } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import { BodyText, ColoredSpan, H4, Spacer } from '../../../../common/src/components/core'
import { stringifyAndRemoveNull } from '../../../../common/src/func'
import {
  FetchFirmDetailsQuery,
  FetchFirmExchangeCredsQuery,
  FetchFirmExchangeCredsQueryVariables,
  FetchFirmGSuiteCredsQuery,
  FetchFirmGSuiteCredsQueryVariables,
  FetchFirmMsGraphCredsQuery,
  FetchFirmMsGraphCredsQueryVariables,
} from '../../../../common/src/types.gen'
import { apolloWebClient, NETWORK_ONLY_FETCH_POLICY } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import FirmCredsEditBox, { Tab } from './FirmCredsEditBox'

const fetchFirmExchangeCreds = gql`
  query FetchFirmExchangeCreds($firmId: Int!) {
    exchangeCreds(firmId: $firmId) {
      ...ExchangeCreds
    }
  }
  ${Fragments.exchangeCreds}
`

const fetchFirmMsGraphCreds = gql`
  query FetchFirmMsGraphCreds($firmId: Int!) {
    msGraphCreds(firmId: $firmId) {
      ...MsGraphCreds
    }
  }
  ${Fragments.msGraphCreds}
`

const fetchFirmGSuiteCreds = gql`
  query FetchFirmGSuiteCreds($firmId: Int!) {
    gSuiteCreds(firmId: $firmId) {
      ...GSuiteCreds
    }
  }
  ${Fragments.gSuiteCreds}
`

const updateFirmExchangeCredsMutation = gql`
  mutation UpdateFirmExchangeCreds($firmId: Int!, $exchangeCreds: ExchangeCredsInput!) {
    updateFirmExchangeCreds(firmId: $firmId, exchangeCreds: $exchangeCreds)
  }
`
const updateFirmMsGraphCredsMutation = gql`
  mutation UpdateFirmMsGraphCredsCreds($firmId: Int!, $msGraphCreds: MsGraphCredsInput!) {
    updateFirmMsGraphCreds(firmId: $firmId, msGraphCreds: $msGraphCreds)
  }
`
const updateFirmGSuiteCredsMutation = gql`
  mutation UpdateFirmGSuiteCreds($firmId: Int!, $gSuiteCreds: GSuiteCredsInput!) {
    updateFirmGSuiteCreds(firmId: $firmId, gSuiteCreds: $gSuiteCreds)
  }
`

interface EmailCalSystemsProps {
  firmInfo: FetchFirmDetailsQuery['firm']
}

interface EmailCalSystemsState {
  tab: Tab
  creds: string
}

export default class EmailCalSystems extends React.PureComponent<EmailCalSystemsProps, EmailCalSystemsState> {
  state = {
    tab: Tab.MSGRAPH,
    creds: '{}',
  }

  componentDidMount() {
    this.setMsGraph()
  }

  render() {
    const { firmInfo } = this.props
    const { tab, creds } = this.state

    return (
      <>
        <H4>Email and calendar system</H4>
        <Spacer height={8} />
        <BodyText>
          Credentials to a Lisa-only email account hosted by the firm. We use this account to talk to agents and bosses
          (not prospects) and send them calendar invites. We hide passwords after you enter them for security.
        </BodyText>
        <Spacer height={16} />

        <Button size="small" color="sky" filled={tab === Tab.MSGRAPH} onClick={this.setMsGraph}>
          Microsoft Graph (Outlook)
        </Button>
        <Spacer width={8} />

        <Button size="small" color="sky" filled={tab === Tab.EXCHANGE} onClick={this.setExchange}>
          Client-hosted Exchange (Outlook)
        </Button>
        <Spacer width={8} />

        <Button size="small" color="sky" filled={tab === Tab.GSUITE} onClick={this.setGSuite}>
          Google Apps Suite
        </Button>

        <Spacer height={8} />
        <FirmCredsEditBox
          firmId={firmInfo.id}
          fetchKey={tab}
          creds={creds}
          fetchOnEdit={this.fetchOnEdit}
          mutateOnSubmit={this.mutateOnSubmit}
        />
        <Spacer height={8} />
        {this.displayOAuthInstructions()}
      </>
    )
  }

  private setExchange = () => {
    const exchangeCreds = this.props.firmInfo.exchangeCredsWithObfuscatedSecrets
    this.setState({
      tab: Tab.EXCHANGE,
      creds: stringifyAndRemoveNull(exchangeCreds),
    })
  }

  private setGSuite = () => {
    const gSuiteCreds = this.props.firmInfo.gSuiteCredsWithObfuscatedSecrets
    this.setState({
      tab: Tab.GSUITE,
      creds: stringifyAndRemoveNull(gSuiteCreds),
    })
  }

  private setMsGraph = () => {
    const msGraphCreds = this.props.firmInfo.msGraphCredsWithObfuscatedSecrets
    this.setState({
      tab: Tab.MSGRAPH,
      creds: stringifyAndRemoveNull(msGraphCreds),
    })
  }
  /**
   * Link the admin to a Google Doc showing how to get an OAuth token for the active-tabbed EmailCalSystem
   */
  private displayOAuthInstructions = () => {
    switch (this.state.tab) {
      case Tab.MSGRAPH:
        return this.msGraphInstructions()
      case Tab.EXCHANGE:
        return this.exchangeInstructions()
      case Tab.GSUITE:
        return this.gSuiteInstructions()
      default:
        return null
    }
  }

  private gSuiteInstructions = () => (
    <BodyText>
      <ColoredSpan color="silver">
        Does this firm use GSuite? Complete setup using{' '}
        <Link to="https://docs.google.com/document/d/1gbMot9xmdWCO732uoxsW2F_Wyc-ioAD81VcWAGp4ITU/edit#heading=h.fv3hxy15cdrr">
          these instructions
        </Link>
        .
      </ColoredSpan>
    </BodyText>
  )

  private exchangeInstructions = () => (
    <BodyText>
      <ColoredSpan color="silver">
        Does this firm host their own Exchange server to run Outlook? Complete setup using{' '}
        <Link to="https://docs.google.com/document/d/1CI_bEc3M-SOI8-MyDBhb2ElvW8GVZ6WMt383dvZQzNY/edit?usp=sharing">
          these instructions
        </Link>
        .
      </ColoredSpan>
    </BodyText>
  )

  private msGraphInstructions = () => (
    <BodyText>
      <ColoredSpan color="silver">
        Does this firm use Office 365 Outlook (Microsoft Graph)? Complete setup using{' '}
        <Link to="https://docs.google.com/document/d/1c-Aui3AlATubT7VGR-WtPUA28pHNjaaY7ye9bRUe0Ls/edit#heading=h.fv3hxy15cdrr">
          these instructions
        </Link>
        .
      </ColoredSpan>
    </BodyText>
  )

  private fetchOnEdit = (firmId: number, fetchKey: Tab) => {
    if (fetchKey === Tab.EXCHANGE) {
      return this.fetchExchange(firmId)
    } else if (fetchKey === Tab.MSGRAPH) {
      return this.fetchMsGraph(firmId)
    } else if (fetchKey === Tab.GSUITE) {
      return this.fetchGSuite(firmId)
    } else {
      return Promise.resolve()
    }
  }

  private fetchExchange = (firmId: number) =>
    apolloWebClient
      .query<FetchFirmExchangeCredsQuery, FetchFirmExchangeCredsQueryVariables>({
        query: fetchFirmExchangeCreds,
        variables: { firmId },
        fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
      })
      .then(res => stringifyAndRemoveNull(res.data.exchangeCreds))

  private fetchMsGraph = (firmId: number) =>
    apolloWebClient
      .query<FetchFirmMsGraphCredsQuery, FetchFirmMsGraphCredsQueryVariables>({
        query: fetchFirmMsGraphCreds,
        variables: { firmId },
        fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
      })
      .then(res => stringifyAndRemoveNull(res.data.msGraphCreds))

  private fetchGSuite = (firmId: number) =>
    apolloWebClient
      .query<FetchFirmGSuiteCredsQuery, FetchFirmGSuiteCredsQueryVariables>({
        query: fetchFirmGSuiteCreds,
        variables: { firmId },
        fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
      })
      .then(res => stringifyAndRemoveNull(res.data.gSuiteCreds))

  private mutateOnSubmit = (firmId: number, fetchKey: Tab, value: string) => {
    if (fetchKey === Tab.EXCHANGE) {
      return this.updateExchange(firmId, value)
    } else if (fetchKey === Tab.MSGRAPH) {
      return this.updateMsGraph(firmId, value)
    } else if (fetchKey === Tab.GSUITE) {
      return this.updateGSuite(firmId, value)
    } else {
      return Promise.resolve()
    }
  }

  private updateExchange = (firmId: number, value: string) => {
    const exchangeCreds = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmExchangeCredsMutation,
      variables: { firmId, exchangeCreds },
    })
  }

  private updateMsGraph = (firmId: number, value: string) => {
    const msGraphCreds = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmMsGraphCredsMutation,
      variables: { firmId, msGraphCreds },
    })
  }

  private updateGSuite = (firmId: number, value: string) => {
    const gSuiteCreds = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmGSuiteCredsMutation,
      variables: { firmId, gSuiteCreds },
    })
  }
}
